"use client";

import React, { useState } from "react";
import { ResponsiveModal, ModalContainer, MODAL_IDS } from "@west-tennessee-consulting/ui";
import { Button } from "@/components/ui/button";
import { Textarea } from "@west-tennessee-consulting/ui";
import { Input } from "@west-tennessee-consulting/ui";
import { useUpdateDeal } from "@/features/deals/hooks/use-deals-hooks";
import type { MarkDealLostModalData } from "@/lib/modals/data/modal-data-types";

// Mounted globally (DetailPageModals) OUTSIDE any detail-action provider, so it
// takes the deal id ONLY from the global-modal-stack data payload (`data.dealId`,
// supplied by the deal `markLost` action handler) — never from React context.
// The write goes through the codegen-typed useUpdateDeal hook.
// See .claude/rules/GLOBAL_MODAL_NO_ACTION_CONTEXT.md + ACTION_MODAL_MOUNTS.md.

export function MarkDealLostModal() {
  return (
    <ModalContainer<MarkDealLostModalData> modalId={MODAL_IDS.deals.markLost}>
      {({ isOpen, data, onClose }) => (
        <MarkDealLostModalContent
          isOpen={isOpen}
          data={data}
          onClose={onClose}
        />
      )}
    </ModalContainer>
  );
}

interface MarkDealLostModalContentProps {
  isOpen: boolean;
  data?: MarkDealLostModalData;
  onClose: () => void;
}

function MarkDealLostModalContent({
  isOpen,
  data,
  onClose,
}: MarkDealLostModalContentProps) {
  const updateDealMutation = useUpdateDeal();

  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const dealId = data?.dealId;

  if (!dealId) return null;

  const handleMarkLost = async () => {
    setIsLoading(true);
    try {
      let lossNotes = "[Lost]";
      if (reason) lossNotes += ` Reason: ${reason}`;
      if (notes) lossNotes += ` - ${notes}`;

      const hasAddendum = Boolean(reason || notes);
      // Append to existing notes (passed via payload) — never clobber.
      const base = (data?.currentNotes ?? "").trim();
      const appendedNotes = base ? `${base}\n\n${lossNotes}` : lossNotes;

      await updateDealMutation.mutateAsync({
        id: dealId,
        stage: "wtc_lost_solution_rejected",
        lost_at: new Date().toISOString(),
        ...(hasAddendum ? { notes: appendedNotes } : {}),
      });

      onClose();
    } catch (error) {
      console.error("Error marking deal as lost:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ResponsiveModal
      globalModalId={MODAL_IDS.deals.markLost}
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Mark Deal as Lost"
      description="Mark this deal as lost"
      maxWidth="sm"
    >
      <div className="p-4">
        <div className="space-y-4">
          <div className="bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 rounded-lg p-3">
            <p className="text-sm text-red-800 dark:text-red-200">
              This deal will be marked as lost and the lost_at timestamp will be recorded.
            </p>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Reason for Loss (Optional)</label>
            <Input
              placeholder="e.g., Budget constraints, Chose competitor, No longer interested..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Additional Notes (Optional)</label>
            <Textarea
              placeholder="Add any additional notes about losing this deal..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleMarkLost}
              disabled={isLoading}
              className="bg-red-600 hover:bg-red-700"
            >
              {isLoading ? "Marking as Lost..." : "Mark as Lost"}
            </Button>
          </div>
        </div>
      </div>
    </ResponsiveModal>
  );
}
